/**
 * Analytics Integration with Plausible
 *
 * Privacy-first analytics without cookies or personal data collection.
 * Only enabled in production when VITE_ENABLE_ANALYTICS=true.
 */

import { env } from '@/config/env'

/**
 * Plausible analytics window interface
 */
declare global {
  interface Window {
    plausible?: (eventName: string, options?: { props?: Record<string, unknown>; callback?: () => void }) => void
  }
}

/**
 * Initialize Plausible Analytics
 * Call this once at app startup
 */
export function initAnalytics(): void {
  if (!env.ENABLE_ANALYTICS || !env.ANALYTICS_DOMAIN) {
    if (env.IS_DEV) {
      console.log('[analytics] Plausible disabled in development')
    }
    return
  }

  // Plausible is loaded via script tag in HTML head
  // This function just validates it's loaded correctly
  if (typeof window !== 'undefined' && window.plausible) {
    if (env.IS_DEV) {
      console.log('[analytics] Plausible initialized')
    }
  }
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, props?: Record<string, string | number | boolean>): void {
  if (!env.ENABLE_ANALYTICS) {
    if (env.IS_DEV) {
      console.log(`[analytics] Event: ${eventName}`, props)
    }
    return
  }

  if (typeof window === 'undefined' || !window.plausible) {
    console.warn('[analytics] Plausible not loaded')
    return
  }

  window.plausible(eventName, { props })
}

/**
 * Track page view (automatic with Plausible, but can be called manually for SPAs)
 */
export function trackPageView(path?: string): void {
  if (!env.ENABLE_ANALYTICS) return

  if (typeof window === 'undefined' || !window.plausible) {
    return
  }

  // Plausible automatically tracks page views, but we can trigger manually
  // if needed for SPA navigation
  if (path) {
    window.plausible('pageview', { props: { path } })
  }
}

/**
 * Common analytics events
 */
export const analytics = {
  /** Track user actions */
  action: (name: string, props?: Record<string, string | number | boolean>) => {
    trackEvent(`Action: ${name}`, props)
  },

  /** Track member actions */
  member: {
    invited: () => trackEvent('Member Invited'),
    enrolled: (planType: string) => trackEvent('Member Enrolled', { planType }),
    cancelled: (planType: string) => trackEvent('Member Cancelled', { planType }),
  },

  /** Track scheduling actions */
  schedule: {
    sessionCreated: (recurrence: string) => trackEvent('Session Created', { recurrence }),
    sessionCancelled: () => trackEvent('Session Cancelled'),
    enrolled: (className: string) => trackEvent('Class Enrolled', { className }),
  },

  /** Track navigation */
  navigation: {
    route: (routeName: string) => trackEvent('Route Visited', { route: routeName }),
  },

  /** Track errors (for non-sensitive data) */
  error: (errorType: string) => trackEvent('Error Occurred', { errorType }),
}

/**
 * Get Plausible script tag for HTML head
 * Use this in your root HTML template
 */
export function getAnalyticsScriptTag(): string {
  if (!env.ENABLE_ANALYTICS || !env.ANALYTICS_DOMAIN) {
    return ''
  }

  return `<script defer data-domain="${env.ANALYTICS_DOMAIN}" src="https://plausible.io/js/script.js"></script>`
}
